import { useEffect, useState } from 'react';
import { fetchProfile } from './profile';

import { LoadingAnimatedPage } from './components/Loading';
import { AdminConfirmationPopup } from './components/AdminConfirmationPopup';
import { usePageTitle } from './hooks/usePageTitle';

interface ProfileData {
  uid: string;
  email: string;
  name?: string;
  username?: string;
  activated: boolean;
  registeredAt: string;
}

export default function Profile() {
  usePageTitle('Profile');
  const [profile, setProfile] = useState<ProfileData | null>(null);
  const [loading, setLoading] = useState(true);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [resettingPassword, setResettingPassword] = useState(false);

  useEffect(() => {
    fetchProfile()
      .then(setProfile)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const handleForgotPassword = async () => {
    if (!profile?.email) return;

    setResettingPassword(true);
    try {
      const res = await fetch('/api/login/passwordreset', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: profile.email }),
      });

      if (!res.ok) throw new Error('Failed to send password reset email');

      setShowConfirmation(false);
      alert('Password reset email has been sent to ' + profile.email);
    } catch (error) {
      console.error('Error requesting password reset:', error);
      alert('Failed to send password reset email. Please try again.');
    } finally {
      setResettingPassword(false);
    }
  };

  if (loading) return <LoadingAnimatedPage />;

  return (
    <div
      id="ts-profile-wrapper"
      className="w-full h-full p-8 flex flex-col items-center justify-start"
    >
      <div
        id="ts-profile-content"
        className="w-2/3 h-full flex flex-col"
      >
        <div className="w-full text-2xl flex items-center justify-start border-b p-2 border-light-border mb-6">
          Profile
        </div>

        {/* Personal Details Section */}
        <div className="mb-4 border border-light-border shadow-sm p-12">
          <h2 className="text-lg font-semibold mb-4 text-light-main">
            Account Details
          </h2>
          <div className="flex flex-col gap-4 pl-4">
            <div className="flex flex-col">
              <label className="text-sm text-light-text/70 mb-1">Name</label>
              <p className="text-light-text">{profile?.name || 'N/A'}</p>
            </div>
            <div className="flex flex-col">
              <label className="text-sm text-light-text/70 mb-1">Email</label>
              <p className="text-light-text">{profile?.email || 'N/A'}</p>
            </div>
            <div className="flex flex-col">
              <label className="text-sm text-light-text/70 mb-1">Status</label>
              <p className="text-light-text">
                {profile?.activated ? 'Activated' : 'Not Activated'}
              </p>
            </div>
            <div className="flex flex-col">
              <label className="text-sm text-light-text/70 mb-1">Registered At</label>
              <p className="text-light-text">
                {new Date(profile?.registeredAt || '').toLocaleString() || 'N/A'}
              </p>
            </div>
          </div>
        </div>

        {/* Security Section */}
        <div className="border border-light-border shadow-sm p-12">
          <h2 className="text-lg font-semibold mb-4 text-light-main">
            Security
          </h2>
          <div className="flex flex-col gap-4 pl-4">
            <p className="text-light-text text-sm">
              Forgot your password? Send a reset link to your email.
            </p>
            <button
              onClick={() => setShowConfirmation(true)}
              disabled={resettingPassword}
              className="w-fit px-4 py-2 bg-light-main text-light-text-alt rounded-sm hover:opacity-90 disabled:opacity-60 cursor-pointer transition-opacity font-medium"
            >
              {resettingPassword ? 'Sending...' : 'Reset Password'}
            </button>
          </div>
        </div>
      </div>

      {showConfirmation && (
        <AdminConfirmationPopup
          innerHTML={
            <div>
              <p>Send a password reset email to {profile?.email}?</p>
            </div>
          }
          onConfirm={handleForgotPassword}
          onCancel={() => setShowConfirmation(false)}
        />
      )}
    </div>
  )
}